import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import type { Ref } from 'vue'

import type { MemberFormValue } from '@/view-models/member'
import { timeUtil } from '@/utils/time'
import type { MemberBirthdayInput } from '@/utils/memberBirthday'
import { getMemberBirthdayRange, normalizeMemberBirthdayInput, sanitizeMemberBirthdayInput } from '@/utils/memberBirthday'

interface UseMemberBirthdayFieldOptions {
  draft: Ref<MemberFormValue>
  birthdayDefaultView: Dayjs
  closePicker: () => void
  validate: () => void
}

export function useMemberBirthdayField({ draft, birthdayDefaultView, closePicker, validate }: UseMemberBirthdayFieldOptions) {
  const birthdayConfig = computed(() => {
    const { min, max } = getMemberBirthdayRange()
    return {
      min: timeUtil.formatDate(min, false, 'YYYY-MM-DD'),
      max: timeUtil.formatDate(max, false, 'YYYY-MM-DD'),
    }
  })
  const normalizedBirthday = computed(() => {
    const birthdayInput: MemberBirthdayInput = typeof draft.value === 'string' ? draft.value : null
    return normalizeMemberBirthdayInput(birthdayInput)
  })
  const parsedBirthdayDraft = computed(() => {
    if (!normalizedBirthday.value) return null

    const birthday = dayjs(normalizedBirthday.value)
    return birthday.isValid() ? birthday : null
  })
  const birthdayPickerDisplayMonth = computed(() => parsedBirthdayDraft.value?.month() ?? birthdayDefaultView.month())
  const birthdayPickerDisplayYear = computed(() => parsedBirthdayDraft.value?.year() ?? birthdayDefaultView.year())
  const selectedBirthdayDate = computed(() => parsedBirthdayDraft.value?.toDate())
  const birthdayConfirmDateFormatted = computed(() => timeUtil.formatDate(normalizedBirthday.value))

  function onBirthdayDateSelected(date: Date) {
    draft.value = timeUtil.formatDate(date, false, 'YYYY-MM-DD')
    closePicker()
    validate()
  }

  function onBirthdayInput(input: MemberBirthdayInput) {
    draft.value = sanitizeMemberBirthdayInput(input)
  }

  function onBirthdayBeforeInput(event: InputEvent) {
    if (event.data && /\D/.test(event.data)) {
      event.preventDefault()
    }
  }

  function onBirthdayPaste(event: ClipboardEvent) {
    event.preventDefault()
    draft.value = sanitizeMemberBirthdayInput(event.clipboardData?.getData('text'))
  }

  function onBirthdayBlur() {
    if (normalizedBirthday.value) {
      draft.value = normalizedBirthday.value
    }
    validate()
  }

  return {
    birthdayConfig,
    birthdayPickerDisplayMonth,
    birthdayPickerDisplayYear,
    normalizedBirthday,
    selectedBirthdayDate,
    birthdayConfirmDateFormatted,
    onBirthdayDateSelected,
    onBirthdayInput,
    onBirthdayBeforeInput,
    onBirthdayPaste,
    onBirthdayBlur,
  }
}
